// src/composables/useWebViewDetection.js
import { ref, onMounted, readonly } from 'vue'

export function useWebViewDetection() {
    // Reactive refs to store the detection results
    const isWebView = ref(false)
    const detectedApp = ref(null) // e.g., 'Facebook', 'LinkedIn', 'Instagram', 'Android WebView', etc.
    const userAgent = ref('') // Store the user agent for potential debugging

    // Function to perform the detection
    const detectWebView = () => {
        // Ensure this runs only on the client-side where navigator is available
        if (typeof window !== 'undefined' && navigator?.userAgent) {
            const ua = navigator.userAgent
            userAgent.value = ua // Store the full user agent
            const uaLower = ua.toLowerCase()

            // --- Define patterns for known WebViews ---
            // Order can matter: Check for more specific identifiers first.
            const patterns = [
                // Specific Apps (adjust keywords as needed based on testing)
                { name: 'Facebook/Messenger', regex: /fban|fbav|fbid|fb_iab|fb4a|messenger/i },
                { name: 'LinkedIn', regex: /linkedin/i },
                { name: 'Instagram', regex: /instagram/i },
                { name: 'Twitter/X', regex: /twitter/i }, // Note: Twitter might be less distinct sometimes
                // General Android WebView (often contains 'wv' in the Chrome string)
                { name: 'Android WebView', regex: /; wv\)/i },
                // iOS WKWebView/UIWebView - Very tricky!
                // Standard iOS WebViews often have user agents *identical* to Safari
                // unless the embedding app *adds* its own identifiers (like FB, LI do).
                // Relying on the *absence* of 'Safari' on iOS is unreliable.
                // We primarily rely on the app-specific markers above for iOS.
                // You could add more patterns for other apps you care about
                // { name: 'SomeOtherApp', regex: /someotherapp_identifier/i },
            ]

            // --- Perform the check ---
            for (const pattern of patterns) {
                if (pattern.regex.test(uaLower)) {
                    isWebView.value = true
                    detectedApp.value = pattern.name
                    // console.log(`WebView detected: ${pattern.name}`); // Optional debug log
                    return // Stop after the first match
                }
            }

            // If no specific pattern matched, it's likely not a *known* WebView.
            // We'll consider it not a WebView for simplicity, unless you have
            // more sophisticated checks (e.g., checking for specific JS interfaces
            // injected by native code, which is app-specific and more complex).
            isWebView.value = false
            detectedApp.value = null
            // console.log('No known WebView pattern matched.'); // Optional debug log
        } else {
            // Cannot detect (e.g., server-side rendering, or non-browser env)
            userAgent.value = 'N/A (SSR or no navigator)'
            isWebView.value = false
            detectedApp.value = null
        }
    }

    // Run detection when the component using this composable mounts
    // Using onMounted ensures navigator.userAgent is available.
    // You could also run it immediately, but onMounted is safer practice.
    onMounted(() => {
        detectWebView()
    })

    // Return readonly refs to prevent modification outside the composable
    // This is good practice for composables providing state.
    return {
        isWebView: readonly(isWebView),
        detectedApp: readonly(detectedApp),
        userAgent: readonly(userAgent), // Expose UA for debugging/info
    }
}
